import React, { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import classnames from 'classnames';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { format, subDays, startOfMonth } from 'date-fns';
import MultiSelect from '../../components/form/MultiSelect';
import Loading from '../../components/Loading';
import FormattedCurrency from '../../components/FormattedCurrency';
import { ReactComponent as CheckIcon } from '../../svg/check.svg';
import { ReactComponent as ChevronLeft } from '../../svg/chevron-left.svg';
import Modal from '../../components/Modal';
import Layout from '../../pages/Layout';
import { getPublishers } from '../../slices/sessionSlice';
import AdminProviderSelect from '../../components/AdminProviderSelect';

export const getSalesTransactions = (search) => {
    return axios.request({
        url: `${process.env.REACT_APP_API_URL}/authorized/transactions`,
        params: search,
        method: 'GET'
    });
};

const getBusinessModels = () => {
    return axios.request({
        url: `${process.env.REACT_APP_API_URL}/authorized/business-models`,
        method: 'GET',
        params: {
            page_size: 1000
        }
    });
};

const TransactionDownloadButton = ({ onSend = () => {} }) => {
    const [sent, setSent] = useState(false);

    const handleSend = async () => {
        try {
            await onSend();
            setSent(true);
        } catch (err) {
            console.error(err);
            setSent(false);
        }
    };
    return (
        <button
            className={classnames('btn btn-sm btn-secondary', {
                'btn-success': sent
            })}
            onClick={handleSend}
            disabled={sent}
        >
            {sent ? (
                <>
                    <CheckIcon /> Request Sent
                </>
            ) : (
                'Request CSV'
            )}
        </button>
    );
};

const getSalesReport = (params) => {
    return axios.request({
        url: `${process.env.REACT_APP_API_URL}/authorized/transactions/download`,
        method: 'GET',
        params: params
    });
};

const prod_partners = [
    { is_active: true, id: 19, name: '24Symbols' },
    { is_active: true, id: 17, name: '3Leaf' },
    { is_active: true, id: 9, name: '3M' },
    { is_active: false, id: 23, name: 'ACX' },
    { is_active: false, id: 38, name: 'Apple' },
    { is_active: false, id: 24, name: 'Audible' },
    { is_active: false, id: 25, name: 'Audiobooks.com' },
    { is_active: true, id: 7, name: 'Baker & Taylor' },
    { is_active: true, id: 33, name: 'Beek' },
    { is_active: true, id: 9, name: 'Bibliotheca' },
    { is_active: true, id: 32, name: 'Chirp' },
    { is_active: false, id: 26, name: 'Downpour' },
    { is_active: true, id: 3, name: 'EBSCO' },
    { is_active: true, id: 6, name: 'eStories' },
    { is_active: true, id: 8, name: 'Follett' },
    { is_active: false, id: 27, name: 'Google' },
    { is_active: false, id: 22, name: 'hibooks' },
    { is_active: false, id: 36, name: 'hoopla' },
    { is_active: false, id: 28, name: 'Hummingbird' },
    { is_active: true, id: 20, name: 'Instaread' },
    { is_active: false, id: 31, name: 'Kobo' },
    { is_active: false, id: 29, name: 'Libro.fm' },
    { is_active: true, id: 21, name: 'Mackin' },
    { is_active: true, id: 5, name: 'Nook' },
    { is_active: true, id: 14, name: 'ODILO' },
    { is_active: false, id: 30, name: 'Overdrive' },
    { is_active: true, id: 15, name: 'Permabound' },
    { is_active: false, id: 10, name: 'Playster' },
    { is_active: false, id: 4, name: 'Scribd' },
    { is_active: false, id: 65, name: 'Spotify' },
    { is_active: true, id: 11, name: 'Wheelers' }
];

const SalesAnalytics = ({ session = {} }) => {
    const [selectedPartnerId, setSelectedPartnerId] = useState([]);
    const [selectedBizModel, setSelectedBizModel] = useState([]);
    const [selectedPublisher, setSelectedPublisher] = useState([]);
    const [dateStart, setDateStart] = useState(subDays(new Date(), 30));
    const [dateEnd, setDateEnd] = useState(new Date());
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [bizModels, setBizModels] = useState([]);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [vendorIds, setVendorIds] = useState(
        session?.user?.provider ? session?.user?.provider?.vendors.map((vendor) => vendor.id) : []
    );
    const [selectedProvider, setSelectedProvider] = useState(session?.user?.provider);
    const [publishers, setPublishers] = useState(session?.user?.publishers || []);
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState();
    const [presetDays, setPresetDays] = useState(30);
    const perPage = 20;
    const selectedChannel = '';

    const columnsMapping = {
        audiobook_title: 'Title',
        isbn: 'ISBN',
        publisher_name: 'Publisher Name',
        partner_reporting_name: 'Distributor',
        channel: 'Channel',
        business_model_reporting_name: 'Business Model',
        territory: 'Territory',
        transaction_date: 'Transaction Date',
        publisher_list_price_amount: 'Publisher List Price Amount',
        publisher_royalty_rate: 'Publisher Royalty Rate',
        quantity: 'Quantity'
    };

    const columnFormatters = {
        publisher_list_price_amount: (value, row) => {
            return value ? <FormattedCurrency value={value} currency={row.publisher_currency || 'USD'} /> : null;
        },
        publisher_royalty_rate: (value, row) => {
            return value ? `${Math.floor(value * 100)}%` : null;
        },
        transaction_date: (value, row) => {
            return value ? format(value, 'MM-DD-YYYY') : null;
        }
    };

    const getTransactions = useCallback(() => {
        setLoadingTransactions(true);

        let query = {
            transaction_date_0: format(dateStart, 'YYYY-MM-DD'),
            transaction_date_1: format(dateEnd, 'YYYY-MM-DD'),
            partner_id: selectedPartnerId.map((p) => p.value).join(',') || null,
            vendor_id: session.isAdmin ? (vendorIds || []).join(',') : session.selectedVendor,
            business_model: selectedBizModel.map((p) => p.value).join(',') || null,
            page_size: perPage,
            page: page,
            publisher_id: selectedPublisher.map((p) => p.value).join(',') || null,
            channel: selectedChannel || null
        };

        getSalesTransactions(query)
            .then((resp) => {
                let { results } = resp.data;

                if (!results.length) {
                    setTransactions([]);

                    return;
                }
                setTransactions(results);
                setPageCount(Math.ceil(resp.data.count / perPage));
            })
            .catch(() => {
                setTransactions([]);
            })
            .finally(() => {
                setLoadingTransactions(false);
            });
    }, [
        dateEnd,
        dateStart,
        perPage,
        page,
        selectedBizModel,
        selectedPartnerId,
        selectedPublisher,
        session.isAdmin,
        session.selectedVendor,
        vendorIds
    ]);

    useEffect(() => {
        getBusinessModels().then((resp) => setBizModels(resp.data.results));
    }, []);

    useEffect(() => {
        getTransactions(null);
    }, [getTransactions]);

    useEffect(() => {
        if (presetDays) {
            let today = new Date();
            if (presetDays === 'month to date') {
                setDateStart(startOfMonth(today));
            } else {
                setDateStart(subDays(today, presetDays));
            }
            setPage(1);
            setDateEnd(today);
        }
    }, [presetDays]);

    const handlePartnerUpdate = (type, val) => {
        setSelectedPartnerId(val);
        setPage(1);
    };

    const handleBizModelUpdate = (type, val) => {
        setPage(1);
        setSelectedBizModel(val);
    };

    const handlePublisherUpdate = (id, val) => {
        setSelectedPublisher(val);
        setPage(1);
    };

    // const handleChannelUpdate = (id, val) => {
    //     setSelectedChannel(val);
    //     setPage(1);
    //     getTransactions();
    // };

    const getReport = async () => {
        let query = {
            transaction_date_0: format(dateStart, 'YYYY-MM-DD'),
            transaction_date_1: format(dateEnd, 'YYYY-MM-DD'),
            partner_id: selectedPartnerId.map((p) => p.value).join(',') || null,
            vendor_id: session.isAdmin ? (vendorIds || []).join(',') : session.selectedVendor,
            business_model: selectedBizModel.map((p) => p.value).join(',') || null,
            page_size: perPage,
            page: page || 1,
            publisher_id: selectedPublisher.map((p) => p.value).join(',') || null,
            channel: selectedChannel || null
        };

        try {
            await getSalesReport(query);
            setShowEmailModal(true);
        } catch (err) {
            console.error(err);
            setShowEmailModal(false);
        }
    };

    const nextPage = () => {
        setPage(page + 1);
    };

    const prevPage = () => {
        setPage(page - 1);
    };

    const updatePublishers = async () => {
        try {
            let provider = selectedProvider;
            let pubs = (await getPublishers(JSON.stringify(provider.publishers))).results || [];
            setPublishers(pubs);
        } catch (err) {
            setPublishers([]);
            console.error(err);
        }
    };

    const handleProviderChange = (id, provider) => {
        setPage(1);
        setSelectedProvider(provider);
        setVendorIds(provider.vendors.map((vendor) => vendor.id));
        setSelectedPublisher([]);
        updatePublishers();
    };

    let { user } = session;

    const title = 'Unofficial Sales';
    return (
        <Layout>
            <section className="dashboard position-relative">
                <Helmet>
                    <title>{title}</title>
                </Helmet>
                <div className="page-header">
                    <p>
                        <Link className="alt-link" to="/reporting">
                            <ChevronLeft width="1em" height="1em" />
                            Back to Royalty Reporting
                        </Link>
                    </p>
                    <h1 className="mb-4">{title}</h1>
                    <p>
                        Unofficial sales data reflects units sold for your titles. For some distributors, this
                        information is updated daily. For the rest, historical data will be available here after it's
                        delivered in official reports. Data usually starts showing up a few weeks after you submit your
                        audiobook for publishing.
                    </p>

                    <p>
                        Note that the information provided in the unofficial sales view is unofficial and is not to be
                        used for calculation of royalty amounts due. While we work hard to ensure the accuracy of this
                        preliminary data, we do not guarantee that this data is error-free. Please refer to the Royalty
                        Reports tab for more information."
                    </p>
                </div>

                <AdminProviderSelect
                    id="providers"
                    label="View Transactions for:"
                    value={selectedProvider?.id || user?.provider?.id || ''}
                    onChange={handleProviderChange}
                    showPublishers
                />

                <div className="row top-md mb-3">
                    <div className="col-xs">
                        <MultiSelect
                            id="publisher"
                            label="Publisher"
                            onChange={handlePublisherUpdate}
                            value={selectedPublisher}
                            options={(publishers || []).map((pub) => ({
                                label: pub.name,
                                value: pub.id
                            }))}
                            small
                            hideOptional
                        />
                    </div>
                    <div className="col-xs">
                        <MultiSelect
                            id="partner"
                            label="Distributor"
                            labelSubtext="* Real-time sales data not available"
                            onChange={handlePartnerUpdate}
                            value={selectedPartnerId}
                            options={prod_partners.map((p) => ({
                                label: `${p.name} ${!p.is_active ? '*' : ''}`,
                                value: p.id
                            }))}
                            small
                            hideOptional
                        />
                    </div>
                    {/* <div className="col-xs">
                        <Select id="channel" label="Channel" onChange={this.handleChannelUpdate} value={this.state.selectedChannel || ''} small hideOptional>
                            <option value="">All</option>
                            {['Retail', 'Library'].map(pub => (
                                <option key={pub} value={pub}>{pub}</option>
                            ))}
                        </Select>
                    </div> */}
                    <div className="col-xs">
                        <MultiSelect
                            id="bizModel"
                            label="Business Model"
                            onChange={handleBizModelUpdate}
                            value={selectedBizModel}
                            options={bizModels.map((bizModel) => ({
                                label: `${bizModel.reporting_name} (${bizModel.name})`,
                                value: bizModel.name
                            }))}
                            small
                            // prependLabel
                            hideOptional
                        />
                    </div>
                </div>
                <div className="row end-md mb-3">
                    <div className="col">
                        <div className="btn-group sales-reporting-date-links mr-0 mb-3">
                            {[7, 30, 45].map((duration) => (
                                <button
                                    key={`last_${duration}_days`}
                                    className={classnames('btn btn-sm', { active: presetDays === duration })}
                                    onClick={() => setPresetDays(duration)}
                                >
                                    Last {duration} Days
                                </button>
                            ))}
                            <button
                                className={classnames('btn btn-sm', { active: presetDays === 'month to date' })}
                                onClick={() => setPresetDays('month to date')}
                            >
                                Month to Date
                            </button>
                        </div>
                        <TransactionDownloadButton onSend={getReport} />
                    </div>
                </div>
                {loadingTransactions && <Loading />}
                {!loadingTransactions && !transactions.length && (
                    <div className="card">
                        No sales have been reported for this time period. Extend the time period or check back again
                        soon for updates.
                    </div>
                )}
                {!!transactions.length && !loadingTransactions && (
                    <>
                        <div className="sales-reporting-table-embed">
                            <table className="table">
                                <thead>
                                    <tr>
                                        {Object.keys(columnsMapping).map((col, ind) => {
                                            if (!columnsMapping[col]) return null;
                                            return <th key={ind}>{columnsMapping[col]}</th>;
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((trans, ind1) => (
                                        <tr key={ind1}>
                                            {Object.keys(columnsMapping).map((field, ind2) => {
                                                return (
                                                    <td key={ind1 + ind2}>
                                                        {columnFormatters[field]
                                                            ? columnFormatters[field](trans[field], trans)
                                                            : trans[field]}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="sales-pagination">
                            <button className="btn btn-sm" onClick={prevPage} disabled={page === 1}>
                                Prev
                            </button>
                            <span>
                                Page {page} of {pageCount}
                            </span>
                            <button className="btn btn-sm" onClick={nextPage} disabled={page >= pageCount}>
                                Next
                            </button>
                        </div>
                    </>
                )}
                <Modal visible={showEmailModal} onClose={(e) => setShowEmailModal(false)}>
                    <p>
                        We've sent an email of your csv to the email address we have on file for you. If you have any
                        questions, contact us at <a href="mailto:pub-support@inaudio.com">pub-support@inaudio.com</a>
                    </p>
                </Modal>
            </section>
        </Layout>
    );
};

export default SalesAnalytics;
